import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  Paper,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { projectsService } from '../../../services/projects.service';
import { teacherService } from '../../../services/teacher.service';
import { getCategories } from '../../../services/categories.services';
import { AdultSponsor } from '../../../types/project.types';
import { Teacher } from '../../../types/teacher.types';
import { getRegistrableFairsInRegion } from '../../../services/fairs.services';
import { Category } from '../../../types/categories.types';

interface CreateProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onProjectCreated: () => void;
}

interface Fair {
  id: string;
  name: string;
  addressLine: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
  startDate: Date;
}

const steps = ['Project Details', 'Class Association', 'Adult Sponsor'];

const CreateProjectDialog: React.FC<CreateProjectDialogProps> = ({
  open,
  onClose,
  onProjectCreated,
}) => {
  const { authStatus } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);

  // Project Details
  const [projectName, setProjectName] = useState('');
  const [fairs, setFairs] = useState<Fair[]>([]);
  const [fairId, setFairId] = useState<string>('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  // Class Association
  const [joinClass, setJoinClass] = useState(false);
  const [classCode, setClassCode] = useState('');
  const [teacher, setTeacher] = useState<Teacher | null>(null);

  // Adult Sponsor
  const [useTeacherAsSponsor, setUseTeacherAsSponsor] = useState(false);
  const [sponsorName, setSponsorName] = useState('');
  const [sponsorEmail, setSponsorEmail] = useState('');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getCategories(authStatus.metadata?.regionId);
        setCategories(data);
      } catch (err) {
        console.error('Failed to fetch categories', err);
      }
    };
    fetchCategories();
  }, [authStatus.metadata?.regionId]);

  const verifyClassCode = async () => {
    if (!classCode.trim()) {
      setError('Please enter a class code');
      return false;
    }

    try {
      setIsVerifyingCode(true);
      setError(null);

      const teacherData = await teacherService.getTeacherByClassCode(classCode);

      if (!teacherData) {
        setError('Invalid class code');
        return false;
      }

      setTeacher(teacherData);
      return true;
    } catch (err) {
      setError('Failed to verify class code');
      return false;
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const fetchFairsToRegister = useCallback(async () => {
    if (!authStatus.metadata?.regionId) return;
    try {
      const rawData = await getRegistrableFairsInRegion(
        authStatus.metadata?.regionId
      );
      if (rawData.length === 0) {
        setError('No fairs available for registration');
        return;
      } else {
        const data: Fair[] = rawData.map((fair: any) => ({
          id: fair.id,
          name: fair.name,
          addressLine: fair.addressLine,
          addressCity: fair.addressCity,
          addressState: fair.addressState,
          addressZip: fair.addressZip,
          startDate: fair.startTime,
          judges: fair.judges || [], // Updated to use judges array
        }));
        setFairs(data);
      }
    } catch (err) {
      setError('Failed to fetch fairs');
    }
  }, [authStatus.metadata?.regionId]);

  useEffect(() => {
    fetchFairsToRegister();
  }, [fetchFairsToRegister]);

  const handleNext = async () => {
    if (activeStep === 0) {
      fetchFairsToRegister();
      if (!projectName.trim()) {
        setError('Please enter a project name');
        return;
      }
      if (selectedCategories.length === 0) {
        setError('Please select a category');
        return;
      }
      setError(null);
      setActiveStep(1);
      return;
    }

    if (activeStep === 1) {
      if (joinClass) {
        const isValid = await verifyClassCode();
        if (!isValid) return;
      }
      setError(null);
      setActiveStep(2);
      return;
    }

    if (activeStep === 2) {
      if (
        !useTeacherAsSponsor &&
        (!sponsorName.trim() || !sponsorEmail.trim())
      ) {
        setError('Please fill in all sponsor information');
        return;
      }
      await handleSubmit();
    }
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!authStatus.user) return;

    try {
      setIsSubmitting(true);
      setError(null);

      const canCreate = await projectsService.canCreateProject(
        authStatus.user.uid
      );
      if (!canCreate) {
        throw new Error('You already have an active project');
      }

      const adultSponsorData: AdultSponsor = {
        name: useTeacherAsSponsor
          ? `${teacher?.firstName || ''} ${teacher?.lastName || ''}`.trim()
          : sponsorName,
        email: useTeacherAsSponsor ? teacher?.email || '' : sponsorEmail,
        isTeacher: useTeacherAsSponsor,
        ...(useTeacherAsSponsor && teacher?.id
          ? { teacherId: teacher.id }
          : {}),
      };

      const classId =
        useTeacherAsSponsor && teacher?.id ? teacher.id : undefined;

      await projectsService.createProject(
        projectName,
        fairId,
        {
          userId: authStatus.user.uid,
          firstName: authStatus.metadata?.firstName || '',
          lastName: authStatus.metadata?.lastName || '',
          email: authStatus.user.email,
          joinedAt: new Date(),
          role: 'creator',
        },
        adultSponsorData,
        classId || undefined,
        selectedCategories
          .map((id) => categories.find((category) => category.id === id)!)
          .filter(Boolean) // Map IDs to category objects
      );

      onProjectCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format date for display to users in a human-readable format
  const formatDateForDisplay = (dateTimeValue: any): string => {
    try {
      let dateTime: Date;

      // Handle Firestore Timestamp
      if (
        dateTimeValue &&
        typeof dateTimeValue === 'object' &&
        'toDate' in dateTimeValue
      ) {
        dateTime = dateTimeValue.toDate();
      } else if (dateTimeValue) {
        dateTime = new Date(dateTimeValue);
      } else {
        return 'Date not set';
      }

      if (isNaN(dateTime.getTime())) return 'Invalid date';

      // Format: April 21, 2025 at 2:30 PM
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      }).format(dateTime);
    } catch (error) {
      console.error('Error formatting date for display:', error);
      return 'Date formatting error';
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Project Name"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              margin="normal"
              required
            />

            <FormControl fullWidth margin="normal" required>
              <InputLabel id="categories-label">Select Categories</InputLabel>
              <Select
                labelId="categories-label"
                multiple
                value={selectedCategories}
                onChange={(e) =>
                  setSelectedCategories(
                    typeof e.target.value === 'string'
                      ? e.target.value.split(',')
                      : e.target.value
                  )
                }
                renderValue={(selected) =>
                  categories
                    .filter((category) => selected.includes(category.id))
                    .map((category) => category.name)
                    .join(', ')
                }
              >
                {categories.length > 0 ? (
                  categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      <Checkbox
                        checked={selectedCategories.includes(category.id)}
                      />
                      {category.name}
                      {category.isHighSchool ? ' - High School' : ''}
                      {category.isElementary ? ' - Elementary School' : ''}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No Categories Available</MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth variant="outlined" margin="dense">
              <InputLabel id="fair-select-label">Fair Select</InputLabel>
              <Select
                labelId="fair-select-label"
                value={fairId}
                onChange={(e) => setFairId(e.target.value)}
                label="Fair Selection"
                displayEmpty
              >
                {fairs.map((fair) => (
                  <MenuItem key={fair.id} value={fair.id}>
                    {fair.name} - {fair.addressCity}, {fair.addressState}
                    {' ('}
                    {formatDateForDisplay(fair.startDate)} {')'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={joinClass}
                  onChange={(e) => {
                    setJoinClass(e.target.checked);
                    if (!e.target.checked) {
                      setClassCode('');
                      setTeacher(null);
                      setUseTeacherAsSponsor(false);
                    }
                  }}
                />
              }
              label="Join a teacher's class"
            />

            {joinClass && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Class Code"
                  value={classCode}
                  onChange={(e) => setClassCode(e.target.value.toUpperCase())}
                  required
                  disabled={isVerifyingCode}
                  InputProps={{
                    startAdornment: isVerifyingCode ? (
                      <CircularProgress size={20} />
                    ) : undefined,
                  }}
                />

                {teacher && (
                  <Paper sx={{ mt: 2, p: 2, bgcolor: 'success.light' }}>
                    <Typography
                      variant="subtitle2"
                      color="success.contrastText"
                    >
                      Teacher Found:
                    </Typography>
                    <Typography color="success.contrastText">
                      {teacher.firstName} {teacher.lastName}
                    </Typography>
                    <Typography variant="body2" color="success.contrastText">
                      {teacher.school}
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            {joinClass && teacher && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useTeacherAsSponsor}
                    onChange={(e) => setUseTeacherAsSponsor(e.target.checked)}
                  />
                }
                label={`Use ${teacher.firstName} ${teacher.lastName} as adult sponsor`}
              />
            )}

            {!useTeacherAsSponsor && (
              <>
                <TextField
                  fullWidth
                  label="Sponsor Name"
                  value={sponsorName}
                  onChange={(e) => setSponsorName(e.target.value)}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Sponsor Email"
                  type="email"
                  value={sponsorEmail}
                  onChange={(e) => setSponsorEmail(e.target.value)}
                  margin="normal"
                  required
                />
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' },
      }}
    >
      <DialogTitle>Create New Project</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mt: 2, mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {activeStep > 0 && <Button onClick={handleBack}>Back</Button>}
        <Button
          onClick={handleNext}
          variant="contained"
          disabled={isSubmitting || isVerifyingCode}
        >
          {isSubmitting ? (
            <CircularProgress size={24} />
          ) : activeStep === steps.length - 1 ? (
            'Create Project'
          ) : (
            'Next'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProjectDialog;
